import type { ZoneCategoryDefinition, ZoneType } from './types.ts';

/**
 * Standard client zone definitions mapping directly to Discriminator zone IDs.
 */
export const ZONE_DEFINITIONS: Record<ZoneType, ZoneCategoryDefinition> = {
  living: {
    id: 'living-id',
    type: 'living',
    nameCs: 'Obývací zóna',
    nameEn: 'Living Zone',
    descriptionCs: 'Centrální společenský prostor pro odpočinek, sledování televize a setkávání s rodinou.',
    descriptionEn: 'Central social area for relaxation, TV entertainment, and family gathering.',
    typicalFurniture: ['Sofa', 'Television', 'Coffee Table', 'Carpet / Rug', 'TV Media Unit'],
    color: 'rgba(59, 130, 246, 0.22)',       // Modern vibrant Blue
    strokeColor: '#3b82f6',
    badgeColor: '#3b82f6',
  },
  dining: {
    id: 'dining-id',
    type: 'dining',
    nameCs: 'Jídelní zóna',
    nameEn: 'Dining Zone',
    descriptionCs: 'Prostor pro stolování, obědy a večeře v blízkosti kuchyně nebo obývacího pokoje.',
    descriptionEn: 'Dining space for meals and family dinners adjacent to kitchen or living area.',
    typicalFurniture: ['Dining Table', 'Chairs', 'Sideboard', 'Dresser / Credenza'],
    color: 'rgba(234, 88, 12, 0.22)',        // Amber / Warm Orange
    strokeColor: '#ea580c',
    badgeColor: '#ea580c',
  },
  kitchen: {
    id: 'kitchen-id',
    type: 'kitchen',
    nameCs: 'Kuchyňská zóna',
    nameEn: 'Kitchen Zone',
    descriptionCs: 'Pracovní plocha pro přípravu a vaření jídla s dostatkem úložného prostoru a spotřebičů.',
    descriptionEn: 'Food preparation and cooking area with countertop workspace and appliances.',
    typicalFurniture: ['Kitchen Counter', 'Refrigerator', 'Cooktop', 'Sink', 'Dishwasher'],
    color: 'rgba(16, 185, 129, 0.22)',       // Emerald Green
    strokeColor: '#10b981',
    badgeColor: '#10b981',
  },
  workplace: {
    id: 'workplace-id',
    type: 'workplace',
    nameCs: 'Pracovní zóna',
    nameEn: 'Workplace Zone',
    descriptionCs: 'Ergonomický koutek pro práci z domova, studium nebo soustředěné činnosti.',
    descriptionEn: 'Ergonomic workspace for home office, focus work, or study.',
    typicalFurniture: ['Desk', 'Office Chair', 'Shelving Unit', 'Desk Lamp'],
    color: 'rgba(168, 85, 247, 0.22)',       // Purple / Violet
    strokeColor: '#a855f7',
    badgeColor: '#a855f7',
  },
  playground: {
    id: 'playground-id',
    type: 'playground',
    nameCs: 'Dětská zóna',
    nameEn: 'Playground Zone',
    descriptionCs: 'Hrací a kreativní koutek pro děti s měkkým kobercem a úložnými boxy na hračky.',
    descriptionEn: 'Play and creativity area for children with soft carpet and toy storage.',
    typicalFurniture: ['Toy Storage Units', 'Play Carpet / Rug', 'Kids Table', 'Small Chairs'],
    color: 'rgba(236, 72, 153, 0.22)',       // Pink / Rose
    strokeColor: '#ec4899',
    badgeColor: '#ec4899',
  },
  relax: {
    id: 'relax-id',
    type: 'relax',
    nameCs: 'Relax zóna',
    nameEn: 'Relax Zone',
    descriptionCs: 'Klidné místo pro čtení knih, poslech hudby a odpočinek s pohodlným křeslem a lampou.',
    descriptionEn: 'Quiet reading and lounge nook with comfortable armchair, lamp, and side table.',
    typicalFurniture: ['Lounge Armchair', 'Side Table', 'Floor Reading Lamp', 'Bookcase'],
    color: 'rgba(20, 184, 166, 0.22)',       // Teal / Cyan
    strokeColor: '#14b8a6',
    badgeColor: '#14b8a6',
  },
};

/**
 * Quick access lookup map by Discriminator zoneId ('living-id' -> ZoneCategoryDefinition).
 */
export const ZONE_ID_TO_DEFINITION_MAP: Record<string, ZoneCategoryDefinition> = Object.values(
  ZONE_DEFINITIONS
).reduce((acc, def) => {
  acc[def.id] = def;
  acc[def.type] = def;
  return acc;
}, {} as Record<string, ZoneCategoryDefinition>);

/**
 * Returns zone category definition by zone ID or type, with fallback.
 */
export function getZoneDefinition(zoneIdOrType: string): ZoneCategoryDefinition {
  const normalized = zoneIdOrType.toLowerCase().trim();
  if (ZONE_ID_TO_DEFINITION_MAP[normalized]) {
    return ZONE_ID_TO_DEFINITION_MAP[normalized];
  }
  
  // Fuzzy match
  for (const [type, def] of Object.entries(ZONE_DEFINITIONS)) {
    if (normalized.includes(type) || normalized.includes(def.id)) {
      return def;
    }
  }

  // Fallback default
  return {
    id: zoneIdOrType,
    type: 'living',
    nameCs: `Zóna ${zoneIdOrType}`,
    nameEn: `Zone ${zoneIdOrType}`,
    descriptionCs: 'Custom Zone',
    descriptionEn: 'Custom Zone',
    typicalFurniture: [],
    color: 'rgba(156, 39, 176, 0.2)',
    strokeColor: '#9c27b0',
    badgeColor: '#9c27b0',
  };
}
